import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FaRegCalendarAlt } from 'react-icons/fa';

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const FISCAL_QUARTERS = [
  { code: 'Q1', months: [3, 4, 5], display: 'Apr – Jun' },
  { code: 'Q2', months: [6, 7, 8], display: 'Jul – Sep' },
  { code: 'Q3', months: [9, 10, 11], display: 'Oct – Dec' },
  { code: 'Q4', months: [0, 1, 2], display: 'Jan – Mar' },
];

const getStartOfWeekSunday = (date) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  next.setDate(next.getDate() - next.getDay());
  return next;
};

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const formatDayMonth = (date) => `${MONTH_SHORT[date.getMonth()]} ${date.getDate()}`;
const formatWeekRange = (weekStart) => `${formatDayMonth(weekStart)} – ${formatDayMonth(addDays(weekStart, 6))}`;

const getFiscalYearLabel = (date) => {
  const year = date.getFullYear();
  const fiscalEndYear = date.getMonth() >= 3 ? year + 1 : year;
  return `FY${String(fiscalEndYear).slice(-2)}`;
};

const getFiscalQuarterInfo = (date) => {
  const month = date.getMonth();
  const quarterIndex = FISCAL_QUARTERS.findIndex((quarter) => quarter.months.includes(month));
  const safeQuarterIndex = quarterIndex >= 0 ? quarterIndex : 0;
  return {
    quarter: FISCAL_QUARTERS[safeQuarterIndex],
    quarterIndex: safeQuarterIndex,
    fiscalYear: getFiscalYearLabel(date),
  };
};

export const getLeadershipFilterOptions = (currentDate = new Date()) => {
  const today = new Date(currentDate);
  const currentWeekStart = getStartOfWeekSunday(today);
  const lastWeekStart = addDays(currentWeekStart, -7);

  const currentMonth = MONTH_LONG[today.getMonth()];
  const lastMonthDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const lastMonth = MONTH_LONG[lastMonthDate.getMonth()];

  const { quarter: currentQuarter, quarterIndex, fiscalYear } = getFiscalQuarterInfo(today);
  const lastQuarterIndex = quarterIndex === 0 ? 3 : quarterIndex - 1;
  const lastQuarter = FISCAL_QUARTERS[lastQuarterIndex];
  const fiscalYearNumeric = Number(fiscalYear.replace('FY', ''));
  const lastQuarterYear = lastQuarterIndex === 3 && quarterIndex === 0
    ? `FY${String(fiscalYearNumeric - 1).padStart(2, '0')}`
    : fiscalYear;

  const lastYearNumeric = fiscalYearNumeric - 1;
  const lastYear = `FY${String(lastYearNumeric).padStart(2, '0')}`;

  return [
    { value: 'Week', label: 'Current Week', display: formatWeekRange(currentWeekStart), group: 'current' },
    { value: 'Month', label: 'Current Month', display: currentMonth, group: 'current' },
    {
      value: 'Quarter',
      label: 'Current Quarter',
      display: `${currentQuarter.code} ${fiscalYear} (${currentQuarter.display})`,
      group: 'current',
    },
    { value: 'Year', label: 'Current Year', display: fiscalYear, group: 'current' },
    { value: 'LastWeek', label: 'Last Week', display: formatWeekRange(lastWeekStart), group: 'last' },
    { value: 'LastMonth', label: 'Last Month', display: lastMonth, group: 'last' },
    {
      value: 'LastQuarter',
      label: 'Last Quarter',
      display: `${lastQuarter.code} ${lastQuarterYear} (${lastQuarter.display})`,
      group: 'last',
    },
    { value: 'LastYear', label: 'Last Year', display: lastYear, group: 'last' },
  ];
};

const PeriodFilterDropdown = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusIndex, setFocusIndex] = useState(-1);
  const containerRef = useRef(null);
  const buttonRef = useRef(null);

  const periodFilters = useMemo(() => getLeadershipFilterOptions(new Date()), []);
  const selected = periodFilters.find((f) => f.value === value) || periodFilters[0];
  const currentColumn = periodFilters.filter((f) => f.group === 'current');
  const lastColumn = periodFilters.filter((f) => f.group === 'last');

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setFocusIndex(-1);
  }, []);

  const selectOption = useCallback(
    (option) => {
      onChange(option.value);
      closeMenu();
      buttonRef.current?.focus();
    },
    [onChange, closeMenu]
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        closeMenu();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, closeMenu]);

  const handleKeyDown = (event) => {
    if (!isOpen) {
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
        event.preventDefault();
        setIsOpen(true);
        setFocusIndex(periodFilters.findIndex((f) => f.value === value));
      }
      return;
    }

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        closeMenu();
        buttonRef.current?.focus();
        break;
      case 'ArrowDown':
        event.preventDefault();
        setFocusIndex((prev) => (prev + 1) % periodFilters.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        setFocusIndex((prev) => (prev <= 0 ? periodFilters.length - 1 : prev - 1));
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (focusIndex >= 0) {
          selectOption(periodFilters[focusIndex]);
        }
        break;
      default:
        break;
    }
  };

  const renderOption = (option) => {
    const globalIndex = periodFilters.findIndex((f) => f.value === option.value);
    const isSelected = value === option.value;
    const isFocused = focusIndex === globalIndex;

    return (
      <button
        key={option.value}
        type="button"
        role="option"
        aria-selected={isSelected}
        className={`lead-period-filter-option ${isSelected ? 'lead-period-filter-option-selected' : ''} ${isFocused ? 'lead-period-filter-option-focused' : ''}`}
        onClick={() => selectOption(option)}
        onMouseEnter={() => setFocusIndex(globalIndex)}
      >
        <span className="lead-period-filter-option-label">{option.label}</span>
        <span className="lead-period-filter-option-date">{option.display}</span>
      </button>
    );
  };

  return (
    <div
      className="lead-period-filter-container"
      ref={containerRef}
      onKeyDown={handleKeyDown}
    >
      <button
        ref={buttonRef}
        type="button"
        className="lead-period-filter-trigger"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
      >
        <FaRegCalendarAlt className="lead-period-filter-icon" aria-hidden="true" />
        <span className="lead-period-filter-label">{selected.label}</span>
        <span className="lead-period-filter-arrow" aria-hidden="true">&#9662;</span>
      </button>

      {isOpen && (
        <div className="lead-period-filter-menu" role="listbox" aria-label="Select time period">
          <div className="lead-period-filter-columns">
            <div className="lead-period-filter-column">
              {currentColumn.map((opt) => renderOption(opt))}
            </div>
            <div className="lead-period-filter-column lead-period-filter-column-right">
              {lastColumn.map((opt) => renderOption(opt))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PeriodFilterDropdown;
